import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check } from "@phosphor-icons/react";
import { type ReactNode } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { kfContentIn } from "@/lib/emotionTw";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import { floatingCollisionPadding } from "../overlayPositioning";

export type DropdownItem = {
  label: ReactNode;
  onSelect?: () => void;
  disabled?: boolean;
  destructive?: boolean;
  checked?: boolean;
};

export type DropdownProps = {
  trigger: ReactNode;
  items: DropdownItem[];
  align?: "start" | "center" | "end";
  size?: "sm" | "md" | "lg";
  className?: string;
};

type DropdownSize = "sm" | "md" | "lg";

/* Exact compiled output of the pre-migration Tailwind utilities, in the same
 * class-list emission order. Verified against the frozen baseline. */

const DropdownContent = styled(DropdownMenu.Content, {
  label: "AgenticDropdown-content"
})({
  zIndex: 1000,
  minWidth: "12rem",
  borderRadius: "var(--bfrs-radius-lg)",
  borderWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  padding: ".25rem",
  "--tw-shadow": "var(--bfrs-shadow-elevated)",
  "--tw-shadow-colored": "var(--bfrs-shadow-elevated)",
  boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)",
  ...kfContentIn,
  animation: "bfrs-bfrs-content-in var(--bfrs-motion-normal) var(--bfrs-ease-out)",
  maxHeight: "min(24rem,var(--radix-dropdown-menu-content-available-height))",
  overflow: "auto"
});

const dropdownItemSize: Record<DropdownSize, CSSObject> = {
  sm: {
    paddingLeft: ".75rem",
    paddingRight: ".75rem",
    paddingTop: ".5rem",
    paddingBottom: ".5rem",
    fontSize: ".75rem",
    lineHeight: "1rem"
  },
  md: {
    paddingLeft: ".75rem",
    paddingRight: ".75rem",
    paddingTop: ".5rem",
    paddingBottom: ".5rem",
    fontSize: ".875rem",
    lineHeight: "1.25rem"
  },
  lg: {
    paddingLeft: "1rem",
    paddingRight: "1rem",
    paddingTop: ".75rem",
    paddingBottom: ".75rem",
    fontSize: ".875rem",
    lineHeight: "1.25rem"
  }
};

interface DropdownItemOwnerState {
  size: DropdownSize;
  destructive: boolean;
  checked: boolean;
}

const DropdownItemRoot = styled(DropdownMenu.Item, {
  label: "AgenticDropdown-item",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: DropdownItemOwnerState }>(({ ownerState }) => [
  {
    display: "flex",
    cursor: "pointer",
    WebkitUserSelect: "none",
    MozUserSelect: "none",
    userSelect: "none",
    alignItems: "center",
    justifyContent: "space-between",
    gap: ".75rem",
    borderRadius: "var(--bfrs-radius-sm)",
    outline: "2px solid transparent",
    outlineOffset: "2px",
    transitionProperty:
      "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
    transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
    transitionDuration: ".15s",
    "&[data-disabled]": {
      pointerEvents: "none",
      cursor: "default",
      "--tw-text-opacity": "1",
      color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
    },
    "&[data-highlighted]": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))"
    }
  },
  dropdownItemSize[ownerState.size],
  ownerState.destructive
    ? {
        "--tw-text-opacity": "1",
        color: "hsl(var(--bfrs-color-danger) / var(--tw-text-opacity, 1))"
      }
    : ownerState.checked
      ? {
          fontWeight: 700,
          "--tw-text-opacity": "1",
          color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
        }
      : {
          "--tw-text-opacity": "1",
          color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
        }
]);

const DropdownItemLabel = styled("span", {
  label: "AgenticDropdown-itemLabel"
})({
  minWidth: "0px",
  flex: "1 1 0%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
});

const DropdownCheckIcon = styled(Check, {
  label: "AgenticDropdown-checkIcon"
})({
  flexShrink: 0,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
});

export function Dropdown({ trigger, items, align = "end", size = "md", className }: DropdownProps) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <BfrsPortalScope>
          <DropdownContent align={align} sideOffset={6} collisionPadding={floatingCollisionPadding} className={className}>
            {items.map((item, index) => (
              <DropdownItemRoot
                key={index}
                disabled={item.disabled}
                onSelect={item.onSelect}
                ownerState={{ size, destructive: Boolean(item.destructive), checked: Boolean(item.checked) }}
              >
                <DropdownItemLabel>{item.label}</DropdownItemLabel>
                {item.checked && <DropdownCheckIcon size={13} weight="bold" />}
              </DropdownItemRoot>
            ))}
          </DropdownContent>
        </BfrsPortalScope>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
